import translation from 'locales/translation';
import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { ParametroCoopRequest, ParametrosTexto } from 'api/Assessor/parametros';
import ErrorHandler from 'helpers/utils/ErrorHandler';

import {
  assessorActionAdicionarItemParametro,
  assessorActionAlterarItemParametro,
  assessorActionAlterarTextoParametro,
  assessorActionCarregarCooperativasParametro,
  assessorActionListarItensParametro,
  assessorActionRemoverItemParametro,
} from 'store/modules/assessor/actions';
import { IAuthReducer } from 'store/modules/AuthReducer';
import { IReducer } from 'store/Reducer';

import { Button, Grid, TextField, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { DropdownIcon, SyncIcon } from 'components/Icons';
import Loading from 'components/Modal/__LEGACY__/AbrirVotacaoPautaModal/Loading';
import PageTitle from 'components/__SHARED__/PageTitle/PageTitle';
import Page from 'components/__SHARED__/PrivateContainer';
import UnauthorizedScreen from 'pages/Errors/UnauthorizedScreen/UnauthorizedScreen';

import LayoutContext from 'components/Layout/LayoutContext';
import { languageValues } from 'helpers/utils/languageUtils';
import { ToastTypes } from 'plataforma-fundacao-componentes';
import { parametrosInitialState, parametrosReducer } from './reducer';
import useStyle from './style';
import Tabela from './Tabela';

export default function Parametros() {
  const classes = useStyle();
  const dispatch = useDispatch();
  const auth = useSelector<IReducer, IAuthReducer>((state) => state.auth);
  const { cooperativasIsLoading, parametrosIsLoading } = useSelector(
    (state: IReducer) => state.assessor
  );
  const [{ pagination, list, currentCoop, items, count }, action] = useReducer(
    parametrosReducer,
    parametrosInitialState
  );

  const { currentEvent } = useSelector((state: IReducer) => state.assembleias);

  const { showToast } = useContext(LayoutContext);

  const getList = useCallback(() => {
    dispatch(
      assessorActionCarregarCooperativasParametro(
        (value) => action({ type: 'setList', value }),
        ErrorHandler
      )
    );
  }, [dispatch]);

  const getItems = useCallback(async () => {
    dispatch(
      assessorActionListarItensParametro(
        pagination.size,
        pagination.page,
        (data) => {
          action({ type: 'setItems', value: data.elements });
          action({ type: 'setCount', value: data.totalElements });
          if (data.totalPages > 0 && pagination.page > data.totalPages) {
            action({
              type: 'setPagination',
              value: {
                ...pagination,
                page: data.totalPages,
              },
            });
          }
        },
        ErrorHandler
      )
    );
  }, [dispatch, pagination]);

  const updateItems = useCallback(
    (changes: ParametroCoopRequest | ParametrosTexto) => {
      const _items = Array.from(items);
      const found = _items.findIndex(
        (item) => item.idParametroCoop === changes.idParametro
      );
      if (found > -1) {
        const update = {
          ...changes,
          idParametro: undefined,
        };
        _items[found] = Object.assign({}, _items[found], update);
        action({ type: 'setItems', value: _items });
      }
    },
    [items]
  );

  const handleInclude = useCallback(
    async (codCooperativa: string) => {
      dispatch(
        assessorActionAdicionarItemParametro(
          codCooperativa,
          () => {
            getList();
            getItems();
            action({ type: 'setCurrentCoop', value: '' });
          },
          ErrorHandler
        )
      );
    },
    [dispatch, getList, getItems]
  );

  const handleChangeParametro = useCallback(
    async (item: ParametroCoopRequest) => {
      dispatch(
        assessorActionAlterarItemParametro(
          item,
          () => {
            toast.success(translation.Parâmetros.toast.sucessoAlteração);
            // getItems();
            updateItems(item);
          },
          (error: any) => {
            showToast({
              pauseOnFocusLoss: true,
              theme: ToastTypes.Error,
              showStatusBar: true,
              timeout: 4000,
              label: languageValues.toasts.error.keyErrors(error!?.message),
            });
          }
        )
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, updateItems]
  );

  const handleChangeParametroTexto = useCallback(
    async (item: ParametrosTexto) => {
      dispatch(
        assessorActionAlterarTextoParametro(
          item,
          () => {
            toast.success(translation.Parâmetros.toast.sucessoAlteração);
            // getItems();
            updateItems(item);
          },
          (error: any) => {
            showToast({
              pauseOnFocusLoss: true,
              theme: ToastTypes.Error,
              showStatusBar: true,
              timeout: 4000,
              label: languageValues.toasts.error.keyErrors(error!?.message),
            });
          }
        )
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, updateItems]
  );

  const handleDelete = useCallback(
    async (idParametroCoop: number) => {
      dispatch(
        assessorActionRemoverItemParametro(
          idParametroCoop,
          () => {
            toast.success(translation.Parâmetros.toast.sucessoExclusão);
            getList();
            getItems();
          },
          (error: any) => {
            showToast({
              pauseOnFocusLoss: true,
              theme: ToastTypes.Error,
              showStatusBar: true,
              timeout: 4000,
              label: languageValues.toasts.error.keyErrors(error!?.message),
            });
          }
        )
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, getList, getItems]
  );

  const handleChangePagination = (size: number, page: number) => {
    action({ type: 'setPagination', value: { size, page } });
  };

  const handleChangeCoopSelected = (value: string) => {
    action({ type: 'setCurrentCoop', value });
  };

  useEffect(() => {
    getList();
    getItems();
  }, [getList, getItems]);

  if (
    (auth?.usuario?.perfil.id === 'pertencer_consulta' &&
      !auth.usuario?.perfil.assessorRoute.duvidasSugestoes.incluirMensagem &&
      currentEvent?.tipo !== 'REU') ||
    !auth.usuario?.perfil.assessorRoute.parametros.ver
  ) {
    return <UnauthorizedScreen />;
  }

  return (
    <Page
      header={
        <React.Fragment>
          <PageTitle title={translation.Parâmetros.title} />
          <Typography component='p' className={classes.subhead}>
            {translation.Parâmetros.subhead}
          </Typography>
        </React.Fragment>
      }
    >
      <Grid container alignItems='flex-end' direction='row' spacing={3}>
        <Grid item xs={4}>
          <Autocomplete
            options={list}
            onChange={(event, value) =>
              handleChangeCoopSelected(value?.codCooperativa || '')
            }
            getOptionLabel={(option) =>
              `${option.codCooperativa} - ${option.nomeCooperativa}`
            }
            getOptionSelected={(option, value) =>
              option.codCooperativa === value.codCooperativa
            }
            clearOnEscape
            renderInput={(params) => (
              <TextField
                {...params}
                label={translation.Parâmetros.inputIncluirLabel}
                margin='normal'
              />
            )}
            noOptionsText={translation.Parâmetros.noOptionsText}
            clearText={translation.Parâmetros.clearText}
            clearOnBlur
            popupIcon={
              cooperativasIsLoading ? (
                <SyncIcon className={classes.selectIcon} animated />
              ) : (
                <DropdownIcon className={classes.selectIcon} />
              )
            }
          />
        </Grid>
        <Grid item xs={3}>
          <Button
            variant='contained'
            color='primary'
            className={classes.botao}
            disabled={currentCoop.length === 0}
            onClick={() => handleInclude(currentCoop)}
          >
            {translation.Parâmetros.buttonIncluirLabel}
          </Button>
        </Grid>
      </Grid>
      <Tabela
        items={items}
        size={pagination.size}
        page={pagination.page}
        count={count}
        onChangeParametro={handleChangeParametro}
        onChangeParametroTexto={handleChangeParametroTexto}
        onDelete={handleDelete}
        onChangePagination={handleChangePagination}
      />
      {parametrosIsLoading && <Loading />}
    </Page>
  );
}
